<template>
  <figure
    class="ProgressBar"
    aria-label="Progress bar"
    :aria-valuemin="0"
    :aria-valuemax="maxValue"
    :aria-valuenow="modelValue"
    :aria-disabled="isDisabled"
  >
    <svg
      ref="svgRef"
      class="ProgressBar__svg"
      v-bind="{height}"
    >
      <defs>
        <clipPath id="progressBar">
          <rect
            ref="progressClipPathRef"
            class="ProgressBar__clipPath"
            x="0"
            y="0"
            v-bind="{width, height}"
            :style="`transform: scaleX(${modelValue / maxValue})`"
          />
        </clipPath>
      </defs>

      <Transition>
        <g
          v-if="modelValue < maxValue"
          class="ProgressBar__tickList"
        >
          <line
            v-for="(_, i) in maxValue"
            :key="i"
            class="ProgressBar__tick--background ProgressBar__tick"
            :x1="i / maxValue * width - 0.5"
            :x2="i / maxValue * width - 0.5"
            :y2="height"
          />
        </g>
      </Transition>

      <rect
        v-show="isReady"
        class="ProgressBar__bar"
        clip-path="url(#progressBar)"
        :width="width"
        :height="height"
      />

      <Transition>
        <g
          v-if="modelValue < maxValue"
          class="ProgressBar__tickList"
        >
          <line
            v-for="(_, i) in maxValue"
            :key="i"
            class="ProgressBar__tick--bar ProgressBar__tick"
            clip-path="url(#progressBar)"
            :x1="i / maxValue * width + 0.5"
            :x2="i / maxValue * width + 0.5"
            :y2="height"
          />
        </g>
      </Transition>
    </svg>

    <Transition>
      <div
        v-if="label"
        class="ProgressBar__label"
      >
        {{ label }}
      </div>
    </Transition>
  </figure>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

const modelValue = defineModel<number>({
  default: 0,
});

const {
  maxValue,
  isReady = true,
} = defineProps<{
  maxValue: number,
  label?: string,
  isDisabled?: boolean,
  isReady?: boolean,
}>();

const svgRef = useTemplateRef("svgRef");
const progressClipPathRef = useTemplateRef("progressClipPathRef");
const width = ref(100);
const height = ref(44);

const resizeObserver = new ResizeObserver(handleResize);

onMounted(() => {
  if (!svgRef.value) return;

  resizeObserver.observe(svgRef.value);
  handleResize();
});

onBeforeUnmount(() => {
  if (!svgRef.value) return;

  resizeObserver.disconnect();
});

watch(() => isReady, () => {
  if (isReady) animateIn();
});

function animateIn () {
  progressClipPathRef.value?.animate([
    {
      transform: "scaleX(0)",
    },
    {
      transform: `scaleX(${Math.min(1, modelValue.value / maxValue)})`,
    },
  ], {
    duration: Math.max(300, Math.min(1_200, 150 * Math.min(8, modelValue.value) * width.value / 320)),
    easing: "cubic-bezier(0.33, 1, 0.68, 1)",
  });
}

function handleResize () {
  if (!svgRef.value) return;

  width.value = svgRef.value.getBoundingClientRect().width;
}
</script>

<style lang="scss" scoped>
.ProgressBar {
  position: relative;

  padding: 3px;

  border-radius: 9999px;
  background-color: var(--themeColorNeutral0);

  &[aria-disabled="true"] {
    background: none;
  }
}

.ProgressBar__svg {
  position: relative;

  width: 100%;
  overflow: hidden;
  container: progressBar / inline-size;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: inherit;
  background-color: inherit;

  .ProgressBar[aria-disabled="true"] & {
    background-color: var(--themeLightTransparent300);
    box-shadow: 0 0 0 3px var(--themeLightTransparent400);
  }
}

.ProgressBar__label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  color: var(--themeColorSecondaryForeground);

  font-size: 18px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .ProgressBar[aria-disabled="true"] & {
    color: var(--themeColorNeutral0);
  }
}

.ProgressBar__tickList {
  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.ProgressBar__tick {
  stroke-dasharray: 2 2;
}

.ProgressBar__tick--background {
  stroke: var(--themeDarkTransparent200);
}

.ProgressBar__tick--bar {
  stroke: var(--themeDarkTransparent200);
}

.ProgressBar__bar {
  fill: var(--themeColorSecondary);

  .ProgressBar[aria-disabled="true"] & {
    display: none;
  }
}

.ProgressBar__clipPath {
  transition: 350ms transform;
}
</style>
